import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Calendar, EventApi } from '@fullcalendar/core';
import { TranslateService } from '@ngx-translate/core';
import { AppointmentService } from 'src/app/services/appointment.service';
import { SwalToastService } from 'src/app/services/swal.service';
import { Subscription, interval, Subject, takeUntil } from 'rxjs';
import { NzModalRef } from 'ng-zorro-antd/modal';
import { CalendarEventsService } from 'src/app/services/calendarEvents.service';
import { PatientService } from 'src/app/services/patient.service';

// Standalone imports

import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { FlexLayoutModule } from '@angular/flex-layout';
import { TranslateModule } from '@ngx-translate/core';
import { NzDividerModule } from 'ng-zorro-antd/divider';

@Component({
  selector: 'app-view-calendar-event',
  standalone: true,
  imports: [
    NzModalModule,
    NzSelectModule,
    NzInputModule,
    NzSwitchModule,
    FormsModule,
    ReactiveFormsModule,
    NzButtonModule,
    TranslateModule,
    FlexLayoutModule,
    NzDatePickerModule,
    NzDividerModule,
  ],
  templateUrl: './view-calendar-event.component.html',
  styleUrls: ['./view-calendar-event.component.css'],
})
export class ViewCalendarEventComponent implements OnInit, OnDestroy {
  @ViewChild('modalRef', { static: true }) modalRef!: NzModalRef;
  // Local variables instead of @Input()
  public event: EventApi | null = null;
  isVisible = false;
  public appointmentStatusList: any[] = [];
  public appointmentServiceTypeList: any[] = [];
  public appointmentReasonsList: any[] = [];
  public slotStatusList: any[] = [];
  public participantStatusList: any[] = [];
  public patients: any[] = [];
  public personnel: any[] = [];
  public departments: any[] = [];
  public isReadOnly = false;

  deletionStarted: boolean = false;
  countdown: number = 5;
  confirmDisabled: boolean = true;
  isRepeated: boolean = false;
  selectedOption: number = 3; // revert to 1

  private timerSubscription?: Subscription;
  private destroy$ = new Subject<void>();

  // Emitter for edit event (if needed)
  public editEventSub: Subject<EventApi> = new Subject<EventApi>();

  constructor(
    private translate: TranslateService,
    private appointmentService: AppointmentService,
    private swalToastService: SwalToastService,
    private calendarEventService: CalendarEventsService,
    private router: Router,
    private patientService: PatientService,
  ) {}

  goToPatientProfile(patientId: string, ev?: Event): void {
    ev?.preventDefault();
    ev?.stopPropagation();
    if (!this.canAccessPatientProfile(patientId)) return;

    this.closeModal();
    this.patientService.resetClinicalData();
    this.router.navigate(['/ehr/profile/' + patientId]);
  }

  canAccessPatientProfile(patientId: string): boolean {
    return this.patients.some(
      (patient) =>
        String(patient.id) === String(patientId) &&
        patient.canAccessProfile === true,
    );
  }

  ngOnInit(): void {
    this.resetState(); // reset local state
    this.calendarEventService.viewCalendarEventModalSub
      .pipe(takeUntil(this.destroy$))
      .subscribe((showModal) => {
        if (!showModal) return;
        this.isVisible = true;

        const modalData = this.calendarEventService.viewCalendarEventModalData;

        this.event = modalData.event || null;
        this.patients = modalData.patients || [];
        this.personnel = modalData.personnel || [];
        this.departments = modalData.departments || [];
        this.isReadOnly = !!modalData.isReadOnly;

        this.appointmentStatusList = modalData.appointmentStatusList || [];
        this.appointmentServiceTypeList =
          modalData.appointmentServiceTypeList || [];
        this.appointmentReasonsList = modalData.appointmentReasonList || [];
        this.slotStatusList = modalData.slotStatusList || [];
        this.participantStatusList = modalData.participantStatusList || [];

        this.isRepeated = modalData.recurrentType !== 1; // Check if the appointment is repeated
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.timerSubscription?.unsubscribe();
    this.modalRef.destroy();
  }

  getStatusName(list: any[], id: any): string {
    if (!Array.isArray(list)) return 'Not set';
    const found = list.find((item) => item.id === id);
    return found ? found.name : 'Not set';
  }

  getRecurrenceTypeName(id: number): string {
    switch (id) {
      case 1:
        return this.translate.instant('Does not repeat');
      case 2:
        return this.translate.instant('days');
      case 3:
        return this.translate.instant('weeks');
      case 4:
        return this.translate.instant('months');
      case 5:
        return this.translate.instant('years');
      default:
        return this.translate.instant('Unknown recurrence type');
    }
  }

  // Called when "Delete" is clicked.
  onDeleteClick(): void {
    this.deletionStarted = true;
    this.startDeletionCountdown();
  }

  startDeletionCountdown(): void {
    this.countdown = 5;
    this.confirmDisabled = true;
    this.timerSubscription = interval(1000).subscribe((sec) => {
      this.countdown = 5 - (sec + 1);
      if (this.countdown <= 0) {
        this.confirmDisabled = false;
        this.timerSubscription?.unsubscribe();
      }
    });
  }

  confirmDeletion(): void {
    const slotId = Number(this.event?.extendedProps['slotId']);
    const appointmentId = Number(this.event?.id);

    const deletion$ = this.isRepeated
      ? this.appointmentService.deleteAppointmentSlots(
          slotId,
          this.selectedOption,
        )
      : this.appointmentService.deleteAppointment(appointmentId);

    deletion$.subscribe({
      next: () => {
        this.calendarEventService.deleteEvent(this.event); // send before clearing
        this.swalToastService.toastSuccess(
          this.translate.instant('Appointment deleted'),
        );
        this.closeModal(); // reset & destroy happens here
      },
      error: (err) => {
        this.swalToastService.toastError(
          `${this.translate.instant(
            'There was an error deleting the appointment',
          )}. ${err.error.message}`,
        );
      },
    });
  }

  onEdit(): void {
    this.calendarEventService.editEvent(this.event!);
    // this.editEventSub.next(this.event!);
    this.modalRef.destroy();
  }

  onOptionChange(newValue: number): void {
    this.selectedOption = newValue;
  }

  closeModal(): void {
    this.calendarEventService.viewCalendarEventModalSub.next(false);
    this.calendarEventService.viewCalendarEventModalData = {};
    this.resetState(); // clear local state
    this.isVisible = false;
    this.modalRef.destroy();
  }
  private resetState(): void {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
      this.timerSubscription = undefined;
    }

    this.deletionStarted = false;
    this.countdown = 5;
    this.confirmDisabled = true;
    this.selectedOption = 3; // revert to 1
    this.event = null;
    this.isRepeated = false;
    this.isReadOnly = false;
  }
}
